import {
	Box,
	createStyles,
	Flex,
	Group,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import type {
	SecodaAIReferencedObjectDetailOut,
	SecodaAIReferencedObjectOut,
} from '@repo/api-codegen';
import { Icon, Text } from '@repo/foundations';

import { BiMetricController } from '../../BiMetric';
import {
	getActionLabel,
	getObjectTypeIcon,
	getObjectTypeLabel,
} from '../utils';

const useStyles = createStyles((theme) => ({
	card: {
		backgroundColor: theme.other.getColor('surface/primary/default'),
		borderRadius: theme.other.borderRadius.md,
		padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		boxShadow: theme.shadows.sm,
		width: '100%',
		maxWidth: '360px',
		'&:hover': {
			backgroundColor: theme.other.getColor('surface/primary/hover'),
		},

		'&:active': {
			backgroundColor: theme.other.getColor('surface/primary/active'),
		},
	},
	cardContent: {
		flex: 1,
	},
	resourceIcon: {
		backgroundColor: 'rgba(0, 0, 0, 0.05)',
		borderRadius: theme.other.borderRadius.sm,
		padding: theme.spacing['3xs'],
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: theme.other.space[7], // 28px
		height: theme.other.space[7], // 28px
	},
	objectTypeIcon: {
		color: theme.other.getColor('icon/primary/default'),
		width: theme.other.space[4], // 16px
		height: theme.other.space[4], // 16px
	},
	chevronIcon: {
		color: theme.other.getColor('icon/secondary/default'),
		width: theme.other.space[5], // 20px
		height: theme.other.space[5], // 20px
	},
	chartContainer: {
		marginTop: theme.spacing.sm,
		minHeight: theme.other.space[60],
	},
}));

export function ReferencedObjectCard({
	referencedObject,
	referencedObjectDetail,
	onReferencedObjectClick,
	shouldShowInlineReferencedObject,
}: {
	referencedObject: SecodaAIReferencedObjectOut;
	referencedObjectDetail?: SecodaAIReferencedObjectDetailOut;
	onReferencedObjectClick?: (
		referencedObject: SecodaAIReferencedObjectOut
	) => void;
	shouldShowInlineReferencedObject?: (
		referencedObject: SecodaAIReferencedObjectOut
	) => boolean;
}) {
	const { classes } = useStyles();

	// Show skeleton if referencedObjectDetail is not available
	if (!referencedObjectDetail) {
		return null;
	}

	if (referencedObjectDetail.is_deleted) {
		return null;
	}

	if (
		referencedObject.object_type === 'BI_METRIC' &&
		shouldShowInlineReferencedObject?.(referencedObject)
	) {
		return (
			<Box className={classes.chartContainer}>
				<BiMetricController
					metricId={referencedObject.object_id}
					mode="view_only"
					onHeaderClick={() => onReferencedObjectClick?.(referencedObject)}
				/>
			</Box>
		);
	}

	return (
		<UnstyledButton
			className={classes.card}
			onClick={() => onReferencedObjectClick?.(referencedObject)}
		>
			<Flex align="center" justify="space-between" gap="sm" wrap="nowrap">
				<Group spacing="sm" noWrap>
					<Box className={classes.resourceIcon}>
						<Icon
							name={getObjectTypeIcon(referencedObject.object_type)}
							className={classes.objectTypeIcon}
						/>
					</Box>
					<Stack spacing={0} className={classes.cardContent}>
						<Text
							size="sm"
							weight="semibold"
							color="text/primary/default"
							lineClamp={1}
						>
							{referencedObjectDetail.object_title}
						</Text>
						<Text
							size="xs"
							color="text/secondary/default"
							fw={450}
							lineClamp={1}
						>
							{getObjectTypeLabel(referencedObject.object_type)}{' '}
							{getActionLabel(referencedObject.action)}
						</Text>
					</Stack>
				</Group>
				{onReferencedObjectClick ? (
					<Icon name="chevronRight" className={classes.chevronIcon} />
				) : (
					<Box />
				)}
			</Flex>
		</UnstyledButton>
	);
}
